// Palworld OFFICIAL CARD GAME "Sleeve & Card Set Vol.1" (SS01) guide cluster.
// Five AEO/SEO articles: product facts, full card list, rules text, parallels
// and collecting/pricing. Every card detail is transcribed from the official
// product reveal images; nothing here is speculation, and anything the official
// pages have not confirmed is labelled as unconfirmed.
import type { Guide } from "./guides-data";

const OFFICIAL = [
  {
    label: "Palworld OFFICIAL CARD GAME — Sleeve & Card Set Vol.1 (EN)",
    url: "https://en.palworld-official-cardgame.com/products/ss01",
    note: "Official English product page: contents, release date and card count.",
  },
  {
    label: "Palworld OFFICIAL CARD GAME — スリーブ＆カードセット Vol.1 (JP)",
    url: "https://palworld-official-cardgame.com/products/ss01",
    note: "Official Japanese product page with the five card reveal images.",
  },
  {
    label: "Palworld OFFICIAL CARD GAME — official card list",
    url: "https://en.palworld-official-cardgame.com/cardlist/",
    note: "Official card list used to verify printed codes and card text.",
  },
];

export const SS01_GUIDES: Guide[] = [
  {
    slug: "palworld-tcg-sleeve-card-set-vol-1",
    title: "Palworld TCG Sleeve & Card Set Vol.1",
    desc: "Every card, sleeve, parallel and price detail in Palworld OFFICIAL CARD GAME Sleeve & Card Set Vol.1 (SS01).",
    tag: "TCG Sets",
    updated: "2026-09-19",
    keywords:
      "palworld sleeve and card set vol 1, palworld ss01 cards, palworld tcg sleeve set, ss01 card list, palworld tcg parallel cards, palworld official card game sleeves",
    articles: [
      {
        title: "Palworld Sleeve & Card Set Vol.1: contents and release",
        slug: "contents-and-release",
        intro:
          "Sleeve & Card Set Vol.1 (product code SS01) is the first Palworld OFFICIAL CARD GAME accessory set that also contains playable cards: 75 standard-size sleeves plus 10 cards made up of 5 brand-new card types at 2 copies each, with 2 of those 10 cards arriving as parallel (foil) versions.",
        sections: [
          {
            heading: "What exactly is in one box?",
            text: "One sealed unit holds 75 standard-size card sleeves (roughly 92 x 67 mm, sized for the same cards you sleeve for constructed play) and 10 cards. The 10 cards are 5 different new cards at 2 copies each, so a single box gives you a full playset of two for every card in the set.",
          },
          {
            heading: "When does it release and what does it cost?",
            text: "The official pages list a Japanese retail price of ¥4,400 including tax per unit, with six-unit retail displays at ¥26,400. The reveal artwork printed an October 2, 2026 date, which was later moved; treat the store date as the one on the official product page and check it before pre-ordering, because the set is accessory-line stock rather than a booster print run.",
          },
          {
            heading: "Are the five cards exclusive to this set?",
            text: "Yes. The official English page states the cards are unique to this product, so the only guaranteed way to obtain SS01-001 through SS01-005 at launch is buying the sleeve set. Official wording also notes cards may later be reprinted or distributed in other products, so exclusivity is not promised forever.",
          },
          {
            heading: "Which Pals are on the cards?",
            text: "Cattiva, Depresso, Chillet, Quivern and Grizzbolt — five of the most recognisable early Pals, each with brand-new art in the set's ink-wash style and each with new card text rather than a reprint of an existing Dawn of Palpagos printing.",
          },
          {
            heading: "Is this a sealed product worth buying twice?",
            text: "For play, one box is enough: two copies of each card is the deck maximum for most cards in the format. A second box only matters if you want spare sleeves or a second shot at pulling the two random parallels, since which two of the ten cards are parallel is randomised.",
          },
        ],
        showcase: "ss01",
        externalLinks: [OFFICIAL[0], OFFICIAL[1], OFFICIAL[2]],
      },
      {
        title: "Full SS01 card list with stats and card text",
        slug: "card-list",
        intro:
          "The five new cards are SS01-001 Grizzbolt, SS01-002 Chillet, SS01-003 Depresso, SS01-004 Cattiva and SS01-005 Quivern. All five are printed at R rarity, and their costs run from 3 to 6 with Power values from 300 to 1300.",
        sections: [
          {
            heading: "SS01-001 Grizzbolt — Raining Lightning",
            text: "Red, cost 6, Power 1000, Strike 2, Normal Pal with Electricity, Crafting, Collecting and Transporting work suitability. On deploy it chooses all of your opponent's Pals and deals 300 damage to each — a board-wide sweeper for chip damage rather than a single big hit.",
          },
          {
            heading: "SS01-002 Chillet — Finishing Ice Blade",
            text: "Blue, cost 6, Power 1000, Strike 2, Normal Pal with Harvesting and Cooling. When the Pal it battled goes to the graveyard, you draw 1 card, so trading up in combat refuels your hand.",
          },
          {
            heading: "SS01-003 Depresso — Midnight Effort",
            text: "Purple, cost 3, Power 300, Strike 1, Normal Pal with Crafting, Collecting, Transporting and Farming. It carries Nocturnal (Power +300 during Night) and, on deploy during Night, hits one Structure for Durability -500 until end of turn.",
          },
          {
            heading: "SS01-004 Cattiva — Full of Confidence",
            text: "Colorless, cost 3, Power 300, Strike 1, Normal Pal with Harvesting, Crafting, Collecting and Transporting. It gains Power +200 at 5 or more Souls and another +200 at 10 or more Souls, scaling into a 700 Power body in a long Soul-heavy game.",
          },
          {
            heading: "SS01-005 Quivern — Wyvern's Breath",
            text: "Colorless, cost 6, Power 1300, Strike 2 and — unusually for an accessory-set card — a Lucky Pal, with Harvesting, Crafting, Collecting and Transporting. Whenever it attacks a Pal or Structure it also deals 700 damage to one Pal or Structure of your choice.",
          },
        ],
        showcase: "ss01",
        externalLinks: [OFFICIAL[1], OFFICIAL[2], OFFICIAL[0]],
      },
      {
        title: "How the SS01 cards play in a real deck",
        slug: "deck-impact",
        intro:
          "SS01 is not a filler accessory drop: Quivern gives colorless decks a 6-cost body that damages twice per attack, and Grizzbolt gives Red a cheap board-wide 300 damage sweep on deploy.",
        sections: [
          {
            heading: "Why is Quivern the strongest card in the set?",
            text: "1300 Power with Strike 2 is already large for cost 6, and the extra 700 damage on every attack lets it clear a small Pal while still connecting with the main target. Being colorless means any deck can play it without bending its color base.",
          },
          {
            heading: "Where does Grizzbolt fit?",
            text: "Any Red deck that struggles against wide boards. 300 damage to every opposing Pal removes low-Power support bodies immediately, and its Electricity work suitability keeps it useful outside combat.",
          },
          {
            heading: "Is Depresso only a Night card?",
            text: "Its Nocturnal bonus and Structure damage both need Night, so it is a build-around, not a generic 3-drop. In a Purple deck that already controls the day/night cycle, a 600 Power 3-cost that pressures Structures is efficient.",
          },
          {
            heading: "Does Cattiva belong in aggressive lists?",
            text: "Not really. Its Power only scales once your Soul count is high, so it rewards decks that bank Souls rather than spend them early. In those decks it is a cheap body that grows into a real attacker.",
          },
          {
            heading: "How do I test these cards before the set ships?",
            text: "Build the shell around your existing cards on /deck-builder, then play it out against the bot or a friend on /tcg-simulator so you know how many slots you actually want to free up for SS01 cards on release day.",
          },
        ],
        externalLinks: [OFFICIAL[2], OFFICIAL[0], OFFICIAL[1]],
      },
      {
        title: "SS01 parallel cards: pull rate, look and value",
        slug: "parallel-cards",
        intro:
          "Every SS01 box contains exactly 2 parallel cards out of the 10 cards inside, and which two are parallel is random — so 8 of your 10 cards are base printings and any of the five card types can arrive foil.",
        sections: [
          {
            heading: "What does an SS01 parallel look like?",
            text: "The parallel printing replaces the base card's white ink-wash background with a full-colour treatment in the card's own colour, and the surface carries a reflective foil finish that shifts as you tilt the card. The gallery in this guide reproduces that tilt so you can compare base and parallel side by side.",
          },
          {
            heading: "Is the parallel rate really 2 in 10?",
            text: "That is what the official product art states: of the 10 enclosed cards, 2 are parallel. It is a guaranteed count rather than a random rate, so every single box yields a pair of foils — the variable is only which cards they are.",
          },
          {
            heading: "How many boxes to complete a full parallel set?",
            text: "Because the two parallels are drawn from five card types, completing all five parallels takes multiple boxes and, statistically, usually more than three. Buying singles is normally the cheaper route once the secondary market settles.",
          },
          {
            heading: "Will parallels hold value?",
            text: "Accessory-set cards historically trade higher than booster commons because the print run is tied to accessory stock rather than a booster reprint cycle. Track actual movement on /prices and the benchmark on /tcg-price-index instead of trusting launch-week asking prices.",
          },
          {
            heading: "How should I store the parallels?",
            text: "Sleeve them immediately — the foil surface scuffs faster than the base matte finish — and keep them in a top loader or the binder pages on /collection rather than mixed loose with playset cards.",
          },
        ],
        showcase: "ss01",
        externalLinks: [OFFICIAL[1], OFFICIAL[0], OFFICIAL[2]],
      },
      {
        title: "Buying SS01 singles, sleeves and sealed boxes",
        slug: "buying-and-prices",
        intro:
          "SS01 arrives as a single SKU, so there are only three ways to get the cards: a sealed sleeve set, singles on the secondary market, or trades — and singles are usually cheaper than chasing a specific parallel through sealed product.",
        sections: [
          {
            heading: "Should I buy sealed or singles?",
            text: "Buy sealed if you want the 75 sleeves as well, since the sleeves alone carry most of the product's utility. Buy singles if you only need the five card types for a deck — two copies each is the whole set, so the singles bill is small and fixed.",
          },
          {
            heading: "Where do I check current prices?",
            text: "Compare asking prices across marketplaces on /prices, use /tcg-price-index for whether the market as a whole is rising or falling, and check completed-sale averages on /tcg-sold-prices before paying a launch-week premium.",
          },
          {
            heading: "How do I order a playset in one go?",
            text: "Drop the card numbers you want into /tcg-bulk-order — paste or import a list, preview the matched cards with images and market prices, then send the whole list to checkout in a single order instead of buying card by card.",
          },
          {
            heading: "Are the sleeves legal for tournament play?",
            text: "They are official Palworld OFFICIAL CARD GAME sleeves at standard size, so they are the intended tournament sleeve. As always, all 50 cards in a deck must use identical, unmarked sleeves — mixing SS01 sleeves with another design is what gets flagged, not the sleeves themselves.",
          },
          {
            heading: "What about condition when buying singles?",
            text: "Parallel foils show edge wear and surface scratches far more than base cards, so ask for photos under direct light. Log purchase prices in /collection so you can see your real cost basis instead of guessing later.",
          },
        ],
        externalLinks: [OFFICIAL[0], OFFICIAL[2], OFFICIAL[1]],
      },
    ],
  },
];
